export { ToastProvider, useToastContext } from './toast';
export type { Toast } from './toast';
export { toastTheme, toastLightTheme, toastDarkTheme } from './toast-theme';
export type { ToastType, ToastPosition, ToastTheme } from './toast-theme';

// Hook for easy toast creation
import { useCallback } from 'react';
import { ReactNode } from 'react';

import { useToastContext } from './toast';
import { ToastType, ToastPosition } from './toast-theme';

export interface UseToastOptions {
  type?: ToastType;
  position?: ToastPosition;
  autoclose?: boolean;
  autocloseTime?: number;
  title?: string;
  description?: string | ReactNode;
}

export const useToast = () => {
  const { addToast, removeToast, clearToasts, closeAll } = useToastContext();

  const showToast = useCallback(
    (message: string | ReactNode, options?: UseToastOptions) => {
      return addToast({
        type: options?.type || 'default',
        message,
        title: options?.title,
        description: options?.description,
        position: options?.position,
        autoclose: options?.autoclose,
        autocloseTime: options?.autocloseTime,
      });
    },
    [addToast],
  );

  const info = useCallback(
    (message: string | ReactNode, options?: Omit<UseToastOptions, 'type'>) => {
      return showToast(message, { ...options, type: 'info' });
    },
    [showToast],
  );

  const success = useCallback(
    (message: string | ReactNode, options?: Omit<UseToastOptions, 'type'>) => {
      return showToast(message, { ...options, type: 'success' });
    },
    [showToast],
  );

  const warning = useCallback(
    (message: string | ReactNode, options?: Omit<UseToastOptions, 'type'>) => {
      return showToast(message, { ...options, type: 'warning' });
    },
    [showToast],
  );

  const error = useCallback(
    (message: string | ReactNode, options?: Omit<UseToastOptions, 'type'>) => {
      return showToast(message, { ...options, type: 'error' });
    },
    [showToast],
  );

  return {
    show: showToast,
    info,
    success,
    warning,
    error,
    remove: removeToast,
    clear: clearToasts,
    closeAll,
  };
};
